import { useEffect, useState } from "react";
import { Type, Check } from "lucide-react";

/**
 * TextSizeControl — controle de escala tipográfica global (Iter179).
 *
 * Multiplica ``document.documentElement.style.fontSize`` para
 * escalar TODA a tipografia baseada em ``rem`` do site (o Tailwind
 * usa rem por padrão). Público-alvo: usuários sêniores, com baixa
 * visão ou fadiga visual — complementa o zoom nativo do browser
 * (Ctrl+/Ctrl-) ao afetar apenas o texto (imagens/logos ficam no
 * mesmo tamanho, preservando o layout).
 *
 * Persistência: ``localStorage["jt.text-size"]`` ∈ {default, large, xlarge}.
 * Sincronização entre instâncias: evento custom "jt:text-size-changed"
 * disparado no window — múltiplos controles (footer + página de
 * acessibilidade) refletem a mesma escolha em tempo real.
 *
 * Não usa atalho de teclado próprio para NÃO colidir com o zoom
 * nativo do browser (Ctrl+/-) — decisão de UX.
 */
const STORAGE_KEY = "jt.text-size";
const LEVELS = [
  { id: "default", label: "Padrão",       short: "A",   scale: 1.0,  hint: "100%" },
  { id: "large",   label: "Grande",       short: "A+",  scale: 1.15, hint: "115%" },
  { id: "xlarge",  label: "Muito grande", short: "A++", scale: 1.3,  hint: "130%" },
];

/** Aplica o tamanho no <html>. Exportado para o bootstrap do app
 *  ler o localStorage e aplicar ANTES do primeiro paint — evita
 *  flash de reajuste de tamanho no LCP. */
export function applyTextSize(id) {
  if (typeof document === "undefined") return;
  const level = LEVELS.find((l) => l.id === id) || LEVELS[0];
  document.documentElement.style.fontSize = `${level.scale * 100}%`;
}

/** Lê a preferência salva. Chamado no bootstrap (index.js). */
export function readSavedTextSize() {
  if (typeof localStorage === "undefined") return "default";
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return LEVELS.find((l) => l.id === v) ? v : "default";
  } catch {
    return "default";
  }
}

export default function TextSizeControl({ compact = false, testIdPrefix = "text-size" }) {
  const [current, setCurrent] = useState("default");

  // Inicializa a partir do localStorage.
  useEffect(() => {
    setCurrent(readSavedTextSize());
  }, []);

  // Escuta mudanças de outras instâncias.
  useEffect(() => {
    const handler = (e) => setCurrent(e.detail || "default");
    window.addEventListener("jt:text-size-changed", handler);
    return () => window.removeEventListener("jt:text-size-changed", handler);
  }, []);

  const choose = (id) => {
    setCurrent(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* noop */ }
    applyTextSize(id);
    window.dispatchEvent(new CustomEvent("jt:text-size-changed", { detail: id }));
  };

  return (
    <div
      className={compact ? "inline-flex items-center gap-1" : "flex items-center gap-2 flex-wrap"}
      role="group"
      aria-label="Tamanho do texto"
      data-testid={`${testIdPrefix}-control`}
    >
      {!compact && (
        <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-slate-500 font-semibold mr-1">
          <Type className="w-3.5 h-3.5" />
          Texto
        </span>
      )}
      {LEVELS.map((l) => {
        const active = current === l.id;
        return (
          <button
            key={l.id}
            type="button"
            onClick={() => choose(l.id)}
            aria-pressed={active}
            aria-label={`Tamanho ${l.label} — ${l.hint}`}
            title={`${l.label} (${l.hint})`}
            data-testid={`${testIdPrefix}-${l.id}`}
            className={
              compact
                ? `inline-flex items-center justify-center px-2 py-1 rounded-full border text-xs font-bold leading-none transition min-w-[28px] ${
                    active
                      ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-100"
                      : "bg-slate-900/70 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                  }`
                : `inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-xs font-semibold transition ${
                    active
                      ? "bg-emerald-500/20 border-emerald-500/60 text-emerald-100"
                      : "bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                  }`
            }
          >
            {compact ? (
              <span translate="no" className="notranslate">{l.short}</span>
            ) : (
              <>
                {active && <Check className="w-3 h-3" />}
                <span translate="no" className="notranslate" style={{ fontSize: `${0.7 + (l.scale - 1) * 0.5}rem` }}>A</span>
                <span className="hidden sm:inline">{l.label}</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
